import { useMemo } from "react";

import type { Token } from "../api/types";
import { formatAge } from "../lib/format";
import * as money from "../lib/money";
import type { AssetBook, Level } from "../store/book";
import { cumulative } from "../store/book";
import { ownLevels, type OwnMark } from "../store/orders";
import { useBooks } from "../store/useBooks";
import { useOrders } from "../store/useOrders";
import { useNow } from "../hooks/useNow";
import { Empty, Panel } from "./Panel";

/** Сколько уровней показываем на сторону. */
export const LADDER_ROWS = 12;

interface Props {
  token: Token;
  /** Клик по уровню подставляет цену в форму ордера. */
  /**
   * Клик по уровню. Несёт И актив, И цену — второе без первого было дефектом:
   * форма держала собственный выбранный исход, и клик по лесенке NO при форме
   * на YES подставлял цену NO в ордер на YES. А купить NO по 0.48 — это продать
   * YES по 0.52, то есть ПРОТИВОПОЛОЖНАЯ позиция.
   */
  onPickPrice?: ((assetId: string, price: string) => void) | undefined;
}

/**
 * Лесенка стакана по одному токену.
 *
 * asks сверху лучшей ценой ВНИЗ, к спреду; bids снизу лучшей ценой вверх —
 * привычная форма биржевого стакана.
 *
 * Число строк на сторону фиксировано и недостающие добиваются пустыми: два
 * стакана рынка стоят рядом, и без выравнивания их строки спреда разъезжались
 * бы по вертикали, а сравнение YES против NO — единственная причина показывать
 * их вместе.
 *
 * Анимации изменения строк здесь сознательно нет: на активном рынке уровни
 * меняются десятки раз в секунду, и подсветка превратилась бы в постоянное
 * мерцание на экране, который смотрят часами.
 */
export function BookLadder({ token, onPickPrice }: Props) {
  const book = useBooks((s) => s.books[token.asset_id]);
  const digits = money.decimalsForTick(book?.tickSize);

  // Актив подставляется здесь, а не у вызывающего: лесенка — единственное
  // место, которое ТОЧНО знает, по какому токену кликнули.
  const pick = useMemo(
    () =>
      onPickPrice === undefined
        ? undefined
        : (price: string) => onPickPrice(token.asset_id, price),
    [onPickPrice, token.asset_id],
  );

  // Наши открытые ордера на этом токене. Это ЭВРИСТИКА по (сторона, цена), а не
  // владение: биржа присылает агрегированные уровни, и узнать в них свои заявки
  // нельзя в принципе. Правила оформления — в docstring ownLevels.
  const orders = useOrders((s) => s.orders);
  const own = useMemo(() => ownLevels(orders, token.asset_id), [orders, token.asset_id]);

  const asks = useMemo(() => book?.asks.slice(0, LADDER_ROWS) ?? [], [book?.asks]);
  const bids = useMemo(() => book?.bids.slice(0, LADDER_ROWS) ?? [], [book?.bids]);

  // Накопленный объём считается ДО разворота — от вершины стакана наружу.
  // Посчитай его после, и «итого» окажется вывернутым.
  const askCum = useMemo(() => cumulative(asks, LADDER_ROWS), [asks]);
  const bidCum = useMemo(() => cumulative(bids, LADDER_ROWS), [bids]);

  const maxCum = Math.max(askCum.at(-1) ?? 0, bidCum.at(-1) ?? 0);

  const askRows = useMemo(
    () => asks.map((level, i) => ({ level, cum: askCum[i] ?? 0 })).reverse(),
    [asks, askCum],
  );
  const bidRows = useMemo(
    () => bids.map((level, i) => ({ level, cum: bidCum[i] ?? 0 })),
    [bids, bidCum],
  );

  return (
    <Panel
      title={token.outcome || `исход ${token.outcome_index}`}
      subtitle={book ? undefined : "загрузка"}
      actions={<Freshness book={book} />}
    >
      {!book || book.status === "waiting" ? (
        <Empty>загрузка стакана…</Empty>
      ) : book.status === "absent" ? (
        <Empty>
          Нет данных: трейдер не публикует стакан по этому токену. Снапшот живёт в Redis 30
          секунд, поэтому пустой ключ означает остановленный или не подписанный на рынок
          трейдер, а не пустой стакан.
        </Empty>
      ) : book.status === "error" ? (
        <p className="py-6 text-center text-sm text-[var(--color-sell)]">{book.error}</p>
      ) : book.bids.length === 0 && book.asks.length === 0 ? (
        <Empty>Стакан пуст: по этому токену нет ни одной заявки.</Empty>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[var(--color-muted)]">
              <th className="pb-1 text-left font-normal">Цена</th>
              <th className="pb-1 text-right font-normal">Размер</th>
              <th className="pb-1 text-right font-normal">Итого</th>
            </tr>
          </thead>
          <tbody>
            {pad(askRows, LADDER_ROWS, "start").map((row, i) =>
              row ? (
                <LadderRow
                  key={`a${row.level.price}`}
                  level={row.level}
                  cum={row.cum}
                  maxCum={maxCum}
                  digits={digits}
                  side="sell"
                  lastTrade={book.lastTradePrice}
                  own={own.asks.get(row.level.p)}
                  onPick={pick}
                />
              ) : (
                <EmptyRow key={`ap${i}`} />
              ),
            )}

            <SpreadRow bids={bids} asks={asks} digits={digits} />

            {pad(bidRows, LADDER_ROWS, "end").map((row, i) =>
              row ? (
                <LadderRow
                  key={`b${row.level.price}`}
                  level={row.level}
                  cum={row.cum}
                  maxCum={maxCum}
                  digits={digits}
                  side="buy"
                  lastTrade={book.lastTradePrice}
                  own={own.bids.get(row.level.p)}
                  onPick={pick}
                />
              ) : (
                <EmptyRow key={`bp${i}`} />
              ),
            )}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

type Row = { level: Level; cum: number };

/** Добивает список до n элементов пустыми местами с нужной стороны. */
function pad(rows: Row[], n: number, side: "start" | "end"): Array<Row | null> {
  const gap: Array<Row | null> = Array.from({ length: Math.max(0, n - rows.length) }, () => null);
  return side === "start" ? [...gap, ...rows] : [...rows, ...gap];
}

function EmptyRow() {
  return (
    <tr>
      <td className="num py-0.5">&nbsp;</td>
      <td />
      <td />
    </tr>
  );
}

function LadderRow({
  level,
  cum,
  maxCum,
  digits,
  side,
  lastTrade,
  own,
  onPick,
}: {
  level: Level;
  cum: number;
  maxCum: number;
  digits: number;
  side: "buy" | "sell";
  lastTrade: string | null;
  own: OwnMark | undefined;
  onPick: ((price: string) => void) | undefined;
}) {
  const color = side === "buy" ? "var(--color-buy)" : "var(--color-sell)";
  // Number() допустим здесь и только здесь: это ширина в CSS, а не арифметика
  // над деньгами (см. правило в api/types.ts).
  const width = maxCum > 0 ? `${(cum / maxCum) * 100}%` : "0%";
  const isLastTrade = lastTrade !== null && lastTrade === level.price;

  return (
    <tr className="relative">
      <td className="num relative py-0.5" style={{ color }}>
        <span
          className="pointer-events-none absolute inset-y-0 left-0 -z-10 rounded-sm opacity-15"
          style={{ width, background: color }}
          aria-hidden
        />
        <span
          className={isLastTrade ? "border-l-2 pl-1" : "pl-1"}
          style={isLastTrade ? { borderColor: "var(--color-accent)" } : undefined}
        >
          {own && <OwnMarker own={own} color={color} />}
          {money.toFixed(level.p, digits)}
        </span>
      </td>
      <td className="num py-0.5 text-right">
        {onPick ? (
          <button
            type="button"
            onClick={() => onPick(level.price)}
            className="hover:underline"
            title="подставить цену в форму ордера"
          >
            {level.size}
          </button>
        ) : (
          level.size
        )}
      </td>
      <td className="num py-0.5 text-right text-[var(--color-muted)]">{money.format(cum)}</td>
    </tr>
  );
}

/**
 * Маркер собственного ордера на уровне.
 *
 * ПОЛЫЙ маркер у PENDING: ордера на бирже ещё нет, и сплошной за секунду до
 * попадания в стакан обещал бы оператору то, чего пока нет, — ровно в том
 * сценарии, который эта веха и принимает («ставит GTC-ордер и видит его в
 * стакане»).
 *
 * Размер уровня рядом НЕ уменьшается на наш: уровень общий, и «300, из них ваши
 * 100» честно, а «200» — вымысел.
 */
function OwnMarker({ own, color }: { own: OwnMark; color: string }) {
  const title =
    `ваш ордер: осталось ${money.format(own.remaining)}` +
    (own.count > 1 ? ` (${own.count} шт.)` : "") +
    (own.pending ? " — ещё не подтверждён биржей" : "");
  return (
    <span
      className="mr-1 inline-block h-2 w-2 rounded-full align-middle"
      style={
        own.pending
          ? { border: `1px solid ${color}`, background: "transparent" }
          : { background: color }
      }
      title={title}
      aria-label={title}
    />
  );
}

function SpreadRow({
  bids,
  asks,
  digits,
}: {
  bids: Level[];
  asks: Level[];
  digits: number;
}) {
  // noUncheckedIndexedAccess заставляет обработать односторонний стакан — и это
  // хорошо: односторонний рынок реален, и молча показать спред «0» было бы ложью.
  const bestBid = bids[0];
  const bestAsk = asks[0];

  if (!bestBid || !bestAsk) {
    return (
      <tr>
        <td
          colSpan={3}
          className="border-y border-[var(--color-border)] py-1 text-center text-[10px] text-[var(--color-muted)]"
        >
          нет двусторонних котировок
        </td>
      </tr>
    );
  }

  const spread = money.sub(bestAsk.p, bestBid.p);
  const mid = money.add(bestBid.p, Math.trunc((spread ?? 0) / 2));

  return (
    <tr>
      <td
        colSpan={3}
        className="num border-y border-[var(--color-border)] py-1 text-center text-[10px] text-[var(--color-muted)]"
      >
        спред {spread === null ? "—" : money.toFixed(spread, digits)}
        {mid !== null && ` · середина ${money.toFixed(mid, digits + 1)}`}
      </td>
    </tr>
  );
}

/**
 * Признак свежести стакана.
 *
 * Живёт отдельным листом дерева и тикает своими часами, чтобы обновление
 * счётчика возраста не перерисовывало всю лесенку.
 */
function Freshness({ book }: { book: AssetBook | undefined }) {
  const now = useNow(1000);
  if (!book) return null;

  if (book.status === "syncing") {
    return (
      <Badge tone="warn">
        синхронизация{book.missed > 0 ? ` · пропущено ${book.missed}` : ""}
      </Badge>
    );
  }
  if (book.status === "absent" || book.status === "error") return null;

  if (book.snapshotStale) return <Badge tone="warn">не подтверждён</Badge>;

  // Возраст считается по часам БРАУЗЕРА от момента применения. Часы трейдера
  // (received_at) для этого непригодны: они расходятся с браузерными, и
  // разность одного с другим была бы уверенной ложью.
  const age = now - book.appliedAt;
  if (book.appliedAt > 0 && age > 30_000) {
    return <Badge tone="warn">нет обновлений {formatAge(age)}</Badge>;
  }
  return <Badge tone="ok">подтверждён</Badge>;
}

function Badge({ tone, children }: { tone: "ok" | "warn"; children: React.ReactNode }) {
  const color = tone === "ok" ? "var(--color-buy)" : "var(--color-warn)";
  return (
    <span className="text-[10px]" style={{ color }}>
      {children}
    </span>
  );
}
